import {useEffect, useMemo, useRef, useState} from 'react';
import {Columns3, RotateCcw} from 'lucide-react';
import Checkbox from 'components/common/checkbox.component';
import {Button, ButtonType} from 'components/common/Button.component';
import {TextInput} from 'components/common/TextInput.component';
import {ICON_SIZE, KEY} from 'components/common/constants';
import type {Translate} from 'types/myTasks';

export interface PickableColumn {
    field: string;
    label: string;
    isVisible: boolean;
}

interface ColumnPickerProps {
    columns: PickableColumn[];
    onToggle: (field: string) => void;
    onReset: () => void;
    isCustomised?: boolean;
    translate: Translate;
}

/**
 * Add / remove the grid's visible columns.
 *
 * ag-grid's own columns tool panel is an Enterprise feature, so this is a plain popover
 * over the same field list the grid builds its columns from. It stays open while columns
 * are toggled, which a DropdownButton would not.
 */
const ColumnPicker = ({columns, onToggle, onReset, isCustomised, translate}: ColumnPickerProps) => {

    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState('');
    const containerRef = useRef<HTMLDivElement>(null);
    const searchRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (!open) return;

        const onClickOutside = (event: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(event.target as Node)) setOpen(false);
        };
        const onEscape = (event: KeyboardEvent) => {
            if (event.key === KEY.ESCAPE) setOpen(false);
        };

        document.addEventListener('mousedown', onClickOutside);
        document.addEventListener('keydown', onEscape);
        return () => {
            document.removeEventListener('mousedown', onClickOutside);
            document.removeEventListener('keydown', onEscape);
        };
    }, [open]);

    useEffect(() => {
        if (open) searchRef.current?.focus();
        else setQuery('');
    }, [open]);

    const visibleCount = columns.filter(c => c.isVisible).length;

    const matching = useMemo(() => {
        const q = query.trim().toLowerCase();
        return q ? columns.filter(c => c.label.toLowerCase().includes(q)) : columns;
    }, [columns, query]);

    // the grid must keep at least one column, so the last one cannot be switched off
    const lastVisible = visibleCount <= 1;

    return (
        <div className="column-picker" ref={containerRef}>
            <Button
                type={ButtonType.SECONDARY}
                onClick={() => setOpen(!open)}
                icon={<Columns3 size={ICON_SIZE.MEDIUM}/>}
                text={translate('myTasks.overview.columns')}
                dataCy="myTasks.columns.toggle"
                ariaLabel={translate('myTasks.overview.columnsAria')}
            />

            {open &&
                <div className="ga-dropdown column-picker__panel" data-cy="myTasks.columns.panel">
                    <div className="column-picker__heading" data-cy="myTasks.columns.heading">
                        {translate('myTasks.overview.columnsHeading', visibleCount, columns.length)}
                    </div>

                    <TextInput
                        ref={searchRef}
                        id="column-picker-search"
                        className="column-picker__search"
                        value={query}
                        onChange={event => setQuery(event.target.value)}
                        onClear={() => setQuery('')}
                        placeholder={translate('myTasks.overview.columnsSearch')}
                        ariaLabel={translate('myTasks.overview.columnsSearch')}
                        clearAriaLabel={translate('myTasks.overview.columnsSearchClear')}
                        dataCy="myTasks.columns.search"
                    />

                    <fieldset className="column-picker__list"
                              aria-label={translate('myTasks.overview.columnsAria')}>
                        {matching.map(column => (
                            <div className="column-picker__item" key={column.field}>
                                <Checkbox
                                    inputId={'column-toggle-' + column.field}
                                    label={column.label}
                                    isChecked={column.isVisible}
                                    isDisabled={column.isVisible && lastVisible}
                                    onChange={() => onToggle(column.field)}
                                    dataCy={'myTasks.columns.option.' + column.field}
                                    ariaLabel={column.label}
                                />
                            </div>
                        ))}
                        {matching.length === 0 &&
                            <div className="column-picker__empty text-disabled">
                                {translate('myTasks.overview.columnsNoMatch')}
                            </div>
                        }
                    </fieldset>

                    {isCustomised &&
                        <div className="column-picker__footer">
                            <Button
                                type={ButtonType.GHOST}
                                onClick={onReset}
                                icon={<RotateCcw size={ICON_SIZE.SMALL}/>}
                                text={translate('myTasks.overview.columnsReset')}
                                dataCy="myTasks.columns.reset"
                            />
                        </div>
                    }
                </div>
            }
        </div>
    );
};

export default ColumnPicker;
